import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { getRedirect, updateRedirect, createRedirect } from './api';
import { useDomain } from './DomainContext';
import PageHeader from './ui/PageHeader';
import Alert from './ui/Alert';
import Spinner from './ui/Spinner';

// For React Router to properly handle IDs with encoded slashes, we need to
// double-encode: React Router decodes once, leaving the API-encoded ID.
const encodeForRouter = (redirectId) => {
  if (redirectId.includes('/')) return encodeURIComponent(encodeURIComponent(redirectId));
  if (redirectId.includes('%2F')) return encodeURIComponent(redirectId);
  return redirectId;
};

const DomainRedirectEditor = () => {
  const { domainId, redirectId } = useParams();
  const navigate = useNavigate();
  const { domains, selectedDomain } = useDomain();
  const [redirect, setRedirect] = useState({
    id: '',
    target_path: '',
    is_gone: false,
    status_code: 301,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const isNewRedirect = redirectId === 'new';

  const domain = selectedDomain?.id === domainId
    ? selectedDomain
    : domains.find((d) => d.id === domainId) || null;
  const hostname = domain?.hostname || domain?.title || '';
  const listPath = `/domain/${domainId}/redirects`;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        if (!isNewRedirect) {
          // useParams() already URL-decodes redirectId; the API layer re-encodes it.
          const redirectData = await getRedirect(domainId, redirectId);
          setRedirect(redirectData);
        }
      } catch (err) {
        if (err.response && err.response.status === 401) {
          navigate('/login');
        } else {
          setError(err.response?.data?.detail || 'Failed to fetch redirect details');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [domainId, redirectId, navigate, isNewRedirect]);

  const handleInputChange = (field, value) => {
    setRedirect((prev) => {
      const updated = { ...prev, [field]: value };
      // Auto-set status code based on is_gone
      if (field === 'is_gone') {
        if (value) {
          updated.status_code = 410;
          updated.target_path = ''; // Clear target path when marking as gone
        } else {
          updated.status_code = 301; // Default back to permanent redirect
        }
      }
      return updated;
    });
    setError(null);
    setSuccessMessage(null);
  };

  const handleSave = async (e) => {
    e?.preventDefault();
    if (!redirect.id.trim()) {
      setError('Source path is required');
      return;
    }
    if (!redirect.is_gone && !redirect.target_path.trim()) {
      setError('Target path is required unless the redirect is marked as gone');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const redirectData = {
        ...redirect,
        id: redirect.id.trim(),
        target_path: redirect.target_path.trim(),
      };

      if (isNewRedirect) {
        const response = await createRedirect(domainId, redirectData);
        setSuccessMessage('Redirect created.');
        const encodedId = response.id || redirectData.id;
        navigate(`${listPath}/${encodeForRouter(encodedId)}/edit`, { replace: true });
      } else {
        await updateRedirect(domainId, redirectId, redirectData);
        setSuccessMessage('Redirect saved.');
      }
    } catch (err) {
      if (err.response && err.response.status === 401) {
        navigate('/login');
      } else {
        setError(err.response?.data?.detail || 'Failed to save redirect');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="page page-narrow">
        <Spinner label="Loading redirect…" />
      </div>
    );
  }

  const title = isNewRedirect ? 'New redirect' : 'Edit redirect';

  return (
    <div className="page page-narrow">
      <PageHeader
        title={hostname ? `${title} · ${hostname}` : title}
        subtitle={isNewRedirect ? undefined : decodeURIComponent(redirect.id || redirectId)}
        actions={<Link to={listPath} className="btn btn-secondary">← Back to redirects</Link>}
      />

      <Alert type="error" onDismiss={() => setError(null)}>{error}</Alert>
      <Alert type="success" onDismiss={() => setSuccessMessage(null)}>{successMessage}</Alert>

      <form className="card" onSubmit={handleSave}>
        <div className="field">
          <label htmlFor="redirect-source">Source path *</label>
          <input
            id="redirect-source"
            type="text"
            className="input mono"
            value={redirect.id}
            onChange={(e) => handleInputChange('id', e.target.value)}
            placeholder="e.g. assets/content/images/old-image.jpg or old-page-slug"
            disabled={!isNewRedirect}
          />
          <span className="hint">
            The path visitors arrive on. It is URL-encoded when stored.
            {!isNewRedirect && ' The source cannot be changed once created.'}
            {domain?.hostname && redirect.id && (
              <>
                {' '}
                <a href={`https://${domain.hostname}/${redirect.id}`} target="_blank" rel="noopener noreferrer">
                  Open source URL ↗
                </a>
              </>
            )}
          </span>
        </div>

        <div className="field">
          <label className="field-inline" style={{ fontWeight: 600 }}>
            <input
              type="checkbox"
              checked={redirect.is_gone}
              onChange={(e) => handleInputChange('is_gone', e.target.checked)}
            />
            Mark as gone (410)
          </label>
          <span className="hint">
            The resource no longer exists and should return a 410 Gone status instead of redirecting.
          </span>
        </div>

        <div className="field">
          <label htmlFor="redirect-target">Target path {!redirect.is_gone && '*'}</label>
          <input
            id="redirect-target"
            type="text"
            className="input mono"
            value={redirect.target_path || ''}
            onChange={(e) => handleInputChange('target_path', e.target.value)}
            placeholder="e.g. /wood-cupcake-stands/"
            disabled={redirect.is_gone}
          />
          <span className="hint">
            {redirect.is_gone
              ? 'Not needed for gone redirects.'
              : 'Where to send visitors. Start with / for a path on this site.'}
            {domain?.hostname && redirect.target_path && !redirect.is_gone && (
              <>
                {' '}
                <a href={`https://${domain.hostname}${redirect.target_path}`} target="_blank" rel="noopener noreferrer">
                  Open target URL ↗
                </a>
              </>
            )}
          </span>
        </div>

        <div className="field">
          <label htmlFor="redirect-status">HTTP status code</label>
          <select
            id="redirect-status"
            className="select"
            value={redirect.status_code}
            onChange={(e) => handleInputChange('status_code', parseInt(e.target.value, 10))}
          >
            <option value={301}>301 – Permanent redirect</option>
            <option value={302}>302 – Temporary redirect</option>
            <option value={410}>410 – Gone</option>
          </select>
          <span className="hint">301 for a permanent move (search engines transfer ranking), 302 for a temporary one.</span>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving…' : isNewRedirect ? 'Create redirect' : 'Save changes'}
          </button>
          <Link to={listPath} className="btn btn-secondary">Cancel</Link>
        </div>
      </form>
    </div>
  );
};

export default DomainRedirectEditor;
